import React, { useLayoutEffect, useRef, useState } from 'react';
import { buildMonthGrid, MONTH_WEEKDAYS } from '../../calendar/monthUtils';
import type { MonthDayRecord, MonthGridCell } from '../../calendar/types';
import {
  MONTH_TIER,
  monthCellMatchesFilter,
  monthDayAtRisk,
  monthHeatTier,
  monthMoneyShort,
} from '../../calendar/monthUtils';
import type { CalendarMonthHeatmap } from '../../calendar/filters';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';

/** Reference month cell — minimum row height (px). */
const MONTH_ROW_MIN_HEIGHT = 200;

const NO_HIDDEN_PARTS: ReadonlySet<string> = new Set();

function MonthBadge({
  icon,
  tone,
  children,
  title,
}: {
  icon: IconKind;
  tone: 'red' | 'gold' | 'ai';
  children: React.ReactNode;
  title?: string;
}) {
  const c = tone === 'red' ? 'var(--terracotta)' : tone === 'gold' ? 'var(--gold)' : 'var(--ai-3)';
  const bg =
    tone === 'red'
      ? 'var(--terracotta-tint)'
      : tone === 'gold'
        ? 'var(--gold-tint)'
        : 'var(--ai-tint)';
  return (
    <span
      title={title}
      className="inline-flex items-center gap-[3px] whitespace-nowrap rounded-[5px] px-1.5 py-px text-[9.5px] font-semibold"
      style={{ background: bg, color: c }}
    >
      <Icon kind={icon} size={9} stroke={2.4} />
      {children}
    </span>
  );
}

function MonthDayCell({
  cell,
  rec,
  isToday,
  dimmed,
  heatmap,
  maxNewPatients,
  hiddenParts,
  onClick,
}: {
  cell: MonthGridCell;
  rec: MonthDayRecord | null;
  isToday: boolean;
  dimmed?: boolean;
  heatmap: CalendarMonthHeatmap;
  maxNewPatients: number;
  hiddenParts: ReadonlySet<string>;
  onClick?: () => void;
}) {
  const [hov, setHov] = useState(false);
  const closed = !cell.off && cell.dow === 0;
  const tier = closed || cell.off ? 'none' : monthHeatTier(rec, heatmap, maxNewPatients);
  const tc = MONTH_TIER[tier];
  const interactive = !cell.off && !closed;
  const atRisk = rec ? monthDayAtRisk(rec) : 0;
  const goalPct = rec ? Math.round((rec.production / (rec.goal || 22000)) * 100) : 0;
  const showProduction = !hiddenParts.has('production');
  const showGoal = !hiddenParts.has('goal');
  const showNewPatients = !hiddenParts.has('newpt');
  const showRisk = !hiddenParts.has('risk');

  let bg = 'var(--cream-1)';
  if (cell.off) bg = 'var(--cream-0)';
  else if (closed) bg = 'var(--surface-mute)';
  else if (rec) bg = tc.tint;
  if (rec?.projected) bg = `color-mix(in srgb, ${tc.tint} 55%, var(--cream-1))`;

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={(ev) => {
        if (interactive && ev.key === 'Enter') onClick?.();
      }}
      onMouseEnter={() => {
        setHov(true);
      }}
      onMouseLeave={() => {
        setHov(false);
      }}
      className={cn(
        'relative flex h-full min-h-[200px] flex-col overflow-hidden px-2.5 py-2 transition-[transform,box-shadow] duration-[160ms] ease-[var(--ease-out)]',
        interactive && 'cursor-pointer',
        !interactive && 'cursor-default',
        isToday &&
          'z-[4] rounded-[4px] outline outline-2 outline-offset-[-2px] outline-[var(--ai-3)]',
        hov && interactive && '-translate-y-px shadow-[var(--sh-2)]',
        (hov && interactive) || isToday ? 'z-[4]' : 'z-[1]'
      )}
      style={{
        background: bg,
        opacity: cell.off ? 0.45 : dimmed ? 0.42 : 1,
      }}
    >
      {interactive && rec && (
        <span
          className="absolute bottom-0 left-0 top-0 w-[3px]"
          style={{ background: tc.bar, opacity: rec.projected ? 0.4 : 0.85 }}
        />
      )}

      <div className="flex items-center justify-between gap-1.5">
        <span
          className={cn(
            'inline-flex items-center justify-center font-[var(--font-display)] font-bold tracking-[-0.03em]',
            isToday
              ? 'h-[26px] w-[26px] rounded-full bg-[var(--ai-tint)] text-[19px] text-[var(--ai-3)]'
              : 'text-base',
            !isToday && cell.off && 'text-[var(--ink-4)]',
            !isToday && !cell.off && 'text-[var(--ink-0)]'
          )}
        >
          {cell.dayNum}
        </span>
        {isToday ? (
          <span className="rounded-full bg-[var(--ai-tint)] px-[5px] py-px text-[8.5px] font-bold tracking-[0.05em] text-[var(--ai-3)]">
            NOW
          </span>
        ) : !cell.off && rec?.projected ? (
          <span className="font-[var(--font-mono)] text-[8.5px] font-semibold uppercase tracking-[0.06em] text-[var(--ink-4)]">
            proj
          </span>
        ) : null}
      </div>

      {closed ? (
        <div className="mt-3.5 text-[10.5px] font-medium italic text-[var(--ink-4)]">Closed</div>
      ) : cell.off ? null : rec ? (
        <>
          {showProduction || showGoal ? (
            <div className="mt-[7px] flex items-baseline gap-1.5">
              {showProduction ? (
                <span
                  className="font-[var(--font-mono)] text-[13px] font-bold tracking-[-0.01em] text-[var(--ink-0)]"
                  style={{ opacity: rec.projected ? 0.65 : 1 }}
                >
                  {monthMoneyShort(rec.production)}
                </span>
              ) : null}
              {showGoal ? (
                <span
                  className="font-[var(--font-mono)] text-[10px] font-semibold"
                  style={{ color: tc.fg, opacity: rec.projected ? 0.7 : 1 }}
                >
                  {goalPct}%
                </span>
              ) : null}
            </div>
          ) : null}
          {showGoal ? (
            <div className="mt-1.5 h-[3px] overflow-hidden rounded-full bg-[var(--cream-2)]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(goalPct, 100)}%`,
                  background: tc.bar,
                  opacity: rec.projected ? 0.45 : 0.9,
                }}
              />
            </div>
          ) : null}
          <div className="mt-2 flex min-h-[17px] flex-wrap items-center gap-[5px]">
            {showRisk && rec.anomaly && (
              <MonthBadge icon="alert" tone="red" title={rec.anomaly}>
                {rec.anomaly.length > 12 ? 'issue' : rec.anomaly}
              </MonthBadge>
            )}
            {showRisk && !rec.anomaly && atRisk >= 5 && (
              <MonthBadge icon="alert" tone="red">
                {atRisk}
              </MonthBadge>
            )}
            {showNewPatients && !rec.projected && rec.newPatients > 0 && (
              <MonthBadge icon="sparkle" tone="ai">
                {rec.newPatients} new
              </MonthBadge>
            )}
            {rec.partial && (
              <MonthBadge icon="clock" tone="gold">
                in progress
              </MonthBadge>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

export interface MonthGridProps {
  year?: number;
  month?: number;
  data?: Record<number, MonthDayRecord>;
  dataByDate?: Record<string, MonthDayRecord>;
  cells?: MonthGridCell[];
  today?: { year: number; month: number; day: number };
  highlightFilter?: string;
  /** Options → Day heatmap: which metric tints each day. Default `goal`. */
  heatmap?: CalendarMonthHeatmap;
  /** Options → Show on cells: parts (`production`, `goal`, `newpt`, `risk`) to hide. */
  hiddenCellParts?: ReadonlySet<string>;
  onSelectDay?: (cell: MonthGridCell, rec: MonthDayRecord | null) => void;
  className?: string;
}

export function MonthGrid({
  year = 2026,
  month = 4,
  data,
  dataByDate,
  cells: cellsProp,
  today = { year: 2026, month: 4, day: 20 },
  highlightFilter = 'all',
  heatmap = 'goal',
  hiddenCellParts = NO_HIDDEN_PARTS,
  onSelectDay,
  className,
}: MonthGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState(MONTH_ROW_MIN_HEIGHT);
  const cells = cellsProp?.length ? cellsProp : buildMonthGrid(year, month);
  const dayData = data ?? {};
  const maxNewPatients = Math.max(
    1,
    ...Object.values(dataByDate ?? dayData).map((r) => r.newPatients),
  );
  const isCur = year === today.year && month === today.month;
  const rows = cells.length / 7;
  const gridBodyHeight = rowHeight * rows;

  const resolveRecord = (cell: MonthGridCell): MonthDayRecord | null => {
    if (cell.off) return null;
    if (cell.date && dataByDate) return dataByDate[cell.date] ?? null;
    return dayData[cell.dayNum] ?? null;
  };

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const syncRowHeight = () => {
      const viewportH = scrollEl.clientHeight;
      const minGridH = MONTH_ROW_MIN_HEIGHT * rows;
      const targetGridH = Math.max(minGridH, viewportH);
      setRowHeight(targetGridH / rows);
    };

    syncRowHeight();
    const ro = new ResizeObserver(syncRowHeight);
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, [rows]);

  return (
    <div
      className={cn(
        'cal-fade flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-2)]',
        className
      )}
      style={{ animationDelay: '150ms' }}
    >
      <div className="grid shrink-0 grid-cols-7 border-b border-[var(--cream-2)] bg-[var(--cream-0)]">
        {MONTH_WEEKDAYS.map((wd, i) => (
          <div
            key={wd}
            className={cn('px-3 py-[19px]', i > 0 && 'border-l border-[var(--cream-2)]')}
          >
            <span
              className={cn(
                'text-[10.5px] font-semibold uppercase tracking-[0.06em]',
                i === 0 ? 'text-[var(--ink-4)]' : 'text-[var(--ink-3)]'
              )}
            >
              {wd}
            </span>
          </div>
        ))}
      </div>
      <div ref={scrollRef} className="cal-scroll relative min-h-0 flex-1 overflow-auto">
        <div
          className="grid gap-px bg-[var(--cream-2)]"
          style={{
            gridTemplateColumns: 'repeat(7, 1fr)',
            gridTemplateRows: `repeat(${rows}, ${rowHeight}px)`,
            height: gridBodyHeight,
            minHeight: '100%',
          }}
        >
          {cells.map((cell, i) => {
            const rec = resolveRecord(cell);
            const isToday =
              cell.date != null
                ? cell.date ===
                  `${String(today.year)}-${String(today.month + 1).padStart(2, '0')}-${String(today.day).padStart(2, '0')}`
                : isCur && !cell.off && cell.dayNum === today.day;
            const matches = monthCellMatchesFilter(rec, highlightFilter);
            const dimmed = highlightFilter !== 'all' && Boolean(rec) && !matches;

            return (
              <MonthDayCell
                key={i}
                cell={cell}
                rec={rec}
                isToday={isToday}
                dimmed={dimmed}
                heatmap={heatmap}
                maxNewPatients={maxNewPatients}
                hiddenParts={hiddenCellParts}
                {...(onSelectDay !== undefined
                  ? {
                      onClick: () => {
                        onSelectDay(cell, rec);
                      },
                    }
                  : {})}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
